/**
 * S9 存档与进度系统 —— 实现 design/gdd/systems/S9-save-progress.md。
 *
 * 单游戏本地存档（localStorage 单键 planet-match3.save.v1）：
 *   持久化关卡进度（通关 / 星级 / 最高分）、充当 S12 设置契约容器、存储 S10 图鉴收集指针。
 *
 * 不含：图鉴内容与解锁逻辑（→ S10）、设置项定义（→ S12）、云存档 / 账号 / 后端（结构性排除：本模块无任何网络路径）。
 */

export type ColorblindMode = 'off' | 'deuteranopia' | 'protanopia' | 'tritanopia';

export interface SettingsV1 {
  version: number;
  /** = userPref（autoActive 是运行时派生值，不存档，S12 §2.6） */
  reducedMotion: boolean;
  colorblindMode: ColorblindMode;
  perfTier: 'auto';
}

export interface LevelRecord {
  levelId: number;
  cleared: boolean;
  stars: number;
  bestScore: number;
}

export interface CodexState {
  unlocked: string[];
  hasNew: boolean;
}

export interface SaveV1 {
  schemaVersion: number;
  savedAt: string | null;
  progress: { levels: LevelRecord[] };
  settings: SettingsV1;
  codex: CodexState;
}

export type WriteErrorKind = 'quota' | 'security' | 'corrupt' | null;
export interface SaveChangedPayload { scope: 'settings' | 'progress' | 'codex'; savedAt: string }
type Listener = (p: SaveChangedPayload) => void;

const KEY_SAVE = 'planet-match3.save.v1';
const KEY_LEGACY = 'planet-match3.settings';
const KEY_CORRUPT = 'planet-match3.save.v1.corrupt';

const DEFAULTS: SaveV1 = {
  schemaVersion: 1,
  savedAt: null,
  progress: { levels: [] },
  settings: { version: 1, reducedMotion: false, colorblindMode: 'off', perfTier: 'auto' },
  codex: { unlocked: [], hasNew: false },
};

function clone<T>(v: T): T { return JSON.parse(JSON.stringify(v)) as T; }

function safeStorage(): Storage | null {
  try {
    const s = window.localStorage;
    const probe = '__pm3_probe__';
    s.setItem(probe, '1');
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
}

export class SaveStore {
  private static _inst: SaveStore | null = null;
  static get instance(): SaveStore {
    if (!SaveStore._inst) SaveStore._inst = new SaveStore();
    return SaveStore._inst;
  }

  private storage: Storage | null;
  private data: SaveV1;
  private writeError: WriteErrorKind = null;
  private listeners = new Set<Listener>();

  /** 是否曾存在存档（用于 E6 首次启动检测；含旧 S12 占位键） */
  readonly hadSavedData: boolean;

  private constructor() {
    this.storage = safeStorage();
    this.hadSavedData = !!(
      this.storage
      && (this.storage.getItem(KEY_SAVE) || this.storage.getItem(KEY_LEGACY))
    );
    this.data = this.load();
  }

  private load(): SaveV1 {
    if (!this.storage) return clone(DEFAULTS);
    this.migrateLegacy();
    const raw = this.storage.getItem(KEY_SAVE);
    if (!raw) return clone(DEFAULTS);
    try {
      return this.fillDefaults(JSON.parse(raw) as Partial<SaveV1>);
    } catch {
      // 存档损坏：原串备份至 .corrupt，全新开局（E2）
      try { this.storage.setItem(KEY_CORRUPT, raw); } catch { /* 忽略 */ }
      this.writeError = 'corrupt';
      console.warn('[save] 存档损坏，已备份并重置为默认');
      return clone(DEFAULTS);
    }
  }

  /** 一次性迁移：旧 S12 占位键 planet-match3.settings → 并入存档 → 删旧键（S9 §2.2 / S12 §2.6） */
  private migrateLegacy(): void {
    if (!this.storage) return;
    const legacy = this.storage.getItem(KEY_LEGACY);
    if (!legacy) return;
    try {
      const l = JSON.parse(legacy) as Partial<SettingsV1>;
      const save: SaveV1 = {
        schemaVersion: 1,
        savedAt: new Date().toISOString(),
        progress: { levels: [] },
        settings: this.fillSettings(l),
        codex: { unlocked: [], hasNew: false },
      };
      this.writeSave(save);
      this.storage.removeItem(KEY_LEGACY);
    } catch {
      // 旧键损坏：忽略，将用 DEFAULTS 覆盖
    }
  }

  private fillSettings(s: Partial<SettingsV1> | undefined): SettingsV1 {
    return {
      version: s?.version ?? 1,
      reducedMotion: !!s?.reducedMotion,
      colorblindMode: (s?.colorblindMode as ColorblindMode) ?? 'off',
      perfTier: 'auto',
    };
  }

  private fillDefaults(p: Partial<SaveV1>): SaveV1 {
    const settings = this.fillSettings(p.settings);
    const levels = Array.isArray(p.progress?.levels)
      ? p.progress!.levels.map((l) => ({
          levelId: l.levelId,
          cleared: !!l.cleared,
          stars: l.stars ?? 0,
          bestScore: l.bestScore ?? 0,
        }))
      : [];
    const codex = {
      unlocked: Array.isArray(p.codex?.unlocked)
        ? p.codex!.unlocked.filter((x): x is string => typeof x === 'string')
        : [],
      hasNew: !!p.codex?.hasNew,
    };
    return {
      schemaVersion: 1,
      savedAt: p.savedAt ?? null,
      progress: { levels },
      settings,
      codex,
    };
  }

  private writeSave(data: SaveV1): void {
    if (!this.storage) return;
    try {
      this.storage.setItem(KEY_SAVE, JSON.stringify(data));
      this.writeError = null;
    } catch (e) {
      // localStorage 不可用 / 配额满（E1）：内存降级，不崩、不阻断
      this.writeError =
        e instanceof DOMException && e.name === 'QuotaExceededError' ? 'quota' : 'security';
    }
  }

  private persist(scope: 'settings' | 'progress' | 'codex'): void {
    this.data.savedAt = new Date().toISOString();
    this.writeSave(this.data);
    const payload: SaveChangedPayload = { scope, savedAt: this.data.savedAt };
    for (const l of [...this.listeners]) {
      try { l(payload); } catch (err) { console.error('[save] listener error', err); }
    }
  }

  // ── 查询 API（S9 §6.1） ──

  getSettings(): SettingsV1 { return { ...this.data.settings }; }

  updateSettings(patch: Partial<SettingsV1>): void {
    this.data.settings = { ...this.data.settings, ...patch, version: 1, perfTier: 'auto' };
    this.persist('settings');
  }

  getLevelRecord(levelId: number): LevelRecord | null {
    return this.data.progress.levels.find((l) => l.levelId === levelId) ?? null;
  }

  /** 线性解锁：cleared 派生，levelId 顺序、无星门（S9 §④） */
  getUnlockedCount(): number {
    const levels = this.data.progress.levels;
    if (levels.length === 0) return 1;
    const maxCleared = Math.max(0, ...levels.filter((l) => l.cleared).map((l) => l.levelId));
    return Math.min(8, 1 + maxCleared);
  }

  isLevelUnlocked(levelId: number): boolean {
    return levelId <= this.getUnlockedCount();
  }

  /** 继续入口（派生，S9 §④） */
  getContinueLevel(): number {
    const incomplete = this.data.progress.levels.find((l) => !l.cleared);
    if (incomplete) return incomplete.levelId;
    return this.getUnlockedCount();
  }

  getCodex(): CodexState {
    return { unlocked: [...this.data.codex.unlocked], hasNew: this.data.codex.hasNew };
  }

  addCodexUnlock(planetId: string): void {
    if (!this.data.codex.unlocked.includes(planetId)) {
      this.data.codex.unlocked.push(planetId);
    }
    this.data.codex.hasNew = true;
    this.persist('codex');
  }

  markCodexViewed(): void {
    if (this.data.codex.hasNew) {
      this.data.codex.hasNew = false;
      this.persist('codex');
    }
  }

  /** 消费 S5 LevelComplete / LevelFailed（S9 §④：星级 / 最高分单调不减） */
  recordLevelResult(
    evt:
      | { type: 'LevelComplete'; levelId: number; stars: number; score: number }
      | { type: 'LevelFailed'; levelId: number; score: number },
  ): void {
    const levels = this.data.progress.levels;
    let entry = levels.find((l) => l.levelId === evt.levelId);
    if (!entry) {
      entry = { levelId: evt.levelId, cleared: false, stars: 0, bestScore: 0 };
      levels.push(entry);
    }
    if (evt.type === 'LevelComplete') {
      entry.cleared = true;
      entry.stars = Math.max(entry.stars, evt.stars);
      entry.bestScore = Math.max(entry.bestScore, evt.score);
    } else {
      // 失败仅更新最高分，不动 cleared / stars（S9 §④ E6）
      entry.bestScore = Math.max(entry.bestScore, evt.score);
    }
    this.persist('progress');
  }

  /** 进度重置（§2.6）：清 progress + codex，不清 settings（可访问性资产） */
  resetProgress(): void {
    this.data.progress = { levels: [] };
    this.data.codex = { unlocked: [], hasNew: false };
    this.persist('progress');
    this.persist('codex');
  }

  getWriteError(): WriteErrorKind { return this.writeError; }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    return () => { this.listeners.delete(l); };
  }
}
